import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { LogOut, FileText, Upload, Calendar as CalendarIcon, Mail, Phone, Edit2, Check, X, Download, List, ChevronLeft, ChevronRight, MessageCircle, Copy, ExternalLink, Trash2, AlertTriangle, Bell, BellRing, BellOff, Volume2, VolumeX, Image as ImageIcon, Facebook, Plus, FileDown } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, parseISO } from 'date-fns';
import { useDropzone } from 'react-dropzone';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Notification sound - longer alarm bell sound
const NOTIFICATION_SOUND = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1cXGFmbmhhaWxycW9wam9xdHd0dXJ1d3h5eHl4d3V0dHJxcnJzdHR1dnZ3d3d3dXRzcnFxcHBxcnN0dXV2dnZ2dnV0c3JxcXBxcnN0dXV2dnZ2dnV0c3JxcHBxcnN0dXZ2d3d3d3Z1dHNycXBwcXJzdHV2dnd3d3d2dXRzcnFwcHFyc3R1dnZ3d3d3dnV0c3JxcXFxcnN0dXV2dnZ2dnV0c3JxcHBxcnN0dXV2dnd3d3Z1dHNycXBwcXJzdHV2d3d4eHd2dXRzcnFwcHFyc3R1dnd3eHh3dnV0c3JxcHBwcXJzdHV2d3d4eHd2dXRzcnFwcHFyc3R1dnd3eHh3dnV0c3FwcHBxcnN0dXZ3eHh4d3Z1dHNycHBwcXJzdHV2d3h4eHd2dXRzcnFwcHFyc3R1dnd4eHh3dnVzcnFwcHBxcnN0dXZ3eHh4d3Z1dHNycXBwcXJzdHV2d3h4eHd2dXRzcnFwcA==';

const AdminDashboard = () => {
    const navigate = useNavigate();
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [vetNotes, setVetNotes] = useState('');
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [followUpDate, setFollowUpDate] = useState('');
    const [noteDate, setNoteDate] = useState('');
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
    const [emailType, setEmailType] = useState('confirmation');
    const [activeTab, setActiveTab] = useState('list');
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDayBookings, setSelectedDayBookings] = useState([]);
    const [isDayDialogOpen, setIsDayDialogOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [bookingToDelete, setBookingToDelete] = useState(null);
    const [isClearAllDialogOpen, setIsClearAllDialogOpen] = useState(false);
    const [isClearing, setIsClearing] = useState(false);
    const [pendingFollowUps, setPendingFollowUps] = useState([]);
    const [editingNoteId, setEditingNoteId] = useState(null);
    const [isAddingNewNote, setIsAddingNewNote] = useState(false);
    
    // Notification states
    const [notificationsEnabled, setNotificationsEnabled] = useState(false);
    const [soundEnabled, setSoundEnabled] = useState(true);
    const [lastCheckTime, setLastCheckTime] = useState(null);
    const [newBookingAlert, setNewBookingAlert] = useState(null);
    const [alarmRinging, setAlarmRinging] = useState(false);
    const audioRef = useRef(null);
    const alarmIntervalRef = useRef(null);
    const lastBookingCountRef = useRef(0);

    const token = localStorage.getItem('token');
    const userEmail = localStorage.getItem('userEmail');

    // Initialize audio
    useEffect(() => {
        audioRef.current = new Audio(NOTIFICATION_SOUND);
        audioRef.current.volume = 0.7;
        audioRef.current.loop = false;
        
        // Check if notifications were previously enabled
        if (Notification.permission === 'granted') {
            setNotificationsEnabled(true);
        }
        
        // Set initial check time
        setLastCheckTime(new Date().toISOString());
    }, []);

    // Request notification permission
    const requestNotificationPermission = async () => {
        if (!('Notification' in window)) {
            toast.error('Notifications not supported in this browser');
            return;
        }
        
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
            setNotificationsEnabled(true);
            toast.success('Notifications enabled! You\'ll be alerted for new bookings.');
            
            // Show test notification
            new Notification('MVC Vet Telecare', {
                body: 'Notifications are now enabled! You\'ll receive alerts for new bookings.',
                icon: '/icons/icon-192x192.png',
                badge: '/icons/icon-72x72.png'
            });
        } else {
            toast.error('Notification permission denied');
        }
    };

    // Start endless alarm
    const startAlarm = useCallback(() => {
        if (soundEnabled && audioRef.current && !alarmRinging) {
            setAlarmRinging(true);
            
            // Play immediately
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(e => console.log('Audio play failed:', e));
            
            // Set up interval to repeat every 2 seconds
            alarmIntervalRef.current = setInterval(() => {
                if (audioRef.current) {
                    audioRef.current.currentTime = 0;
                    audioRef.current.play().catch(e => console.log('Audio play failed:', e));
                }
            }, 2000);
        }
    }, [soundEnabled, alarmRinging]);

    // Stop alarm
    const stopAlarm = useCallback(() => {
        setAlarmRinging(false);
        if (alarmIntervalRef.current) {
            clearInterval(alarmIntervalRef.current);
            alarmIntervalRef.current = null;
        }
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
        setNewBookingAlert(null);
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (alarmIntervalRef.current) {
                clearInterval(alarmIntervalRef.current);
            }
        };
    }, []);

    // Show browser notification
    const showBrowserNotification = useCallback((booking) => {
        if (notificationsEnabled && Notification.permission === 'granted') {
            const notification = new Notification('🐾 New Booking!', {
                body: `${booking.client?.full_name || 'Client'} booked for ${booking.pet?.name || 'pet'}\nRef: ${booking.booking_reference}`,
                icon: '/icons/icon-192x192.png',
                badge: '/icons/icon-72x72.png',
                tag: booking.booking_reference,
                requireInteraction: true
            });
            
            notification.onclick = () => {
                window.focus();
                notification.close();
                stopAlarm();
            };
        }
    }, [notificationsEnabled, stopAlarm]);

    // Check for new bookings periodically
    useEffect(() => {
        const checkNewBookings = async () => {
            if (!token || alarmRinging) return;
            
            try {
                const response = await axios.get(`${API}/bookings/with-notes`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                
                const currentCount = response.data.length;
                
                // If we have more bookings than before, show notification
                if (lastBookingCountRef.current > 0 && currentCount > lastBookingCountRef.current) {
                    const newBooking = response.data[0]; // Latest booking
                    
                    // Start endless alarm
                    startAlarm();
                    
                    // Show browser notification
                    showBrowserNotification(newBooking);
                    
                    // Show in-app alert
                    setNewBookingAlert(newBooking);
                    toast.success(`New booking from ${newBooking.client?.full_name}!`, {
                        duration: 10000,
                        icon: '🐾'
                    });
                    
                    // Update bookings list
                    setBookings(response.data);
                }
                
                lastBookingCountRef.current = currentCount;
            } catch (error) {
                console.log('Error checking new bookings:', error);
            }
        };
        
        // Check every 10 seconds
        const interval = setInterval(checkNewBookings, 10000);
        
        return () => clearInterval(interval);
    }, [token, startAlarm, showBrowserNotification, alarmRinging]);

    useEffect(() => {
        fetchBookings();
        fetchPendingFollowUps();
    }, []);

    const fetchBookings = async () => {
        try {
            const response = await axios.get(`${API}/bookings/with-notes`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBookings(response.data);
        } catch (error) {
            toast.error('Failed to fetch bookings');
        } finally {
            setLoading(false);
        }
    };

    // Fetch pending follow-ups
    const fetchPendingFollowUps = async () => {
        try {
            const response = await axios.get(`${API}/follow-ups/pending`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setPendingFollowUps(response.data);
        } catch (error) {
            console.log('Error fetching follow-ups:', error);
        }
    };

    const { getRootProps, getInputProps } = useDropzone({
        accept: { 'application/pdf': ['.pdf'], 'image/*': ['.png', '.jpg', '.jpeg'] },
        onDrop: async (acceptedFiles) => {
            try {
                const uploadPromises = acceptedFiles.map(async (file) => {
                    const formData = new FormData();
                    formData.append('file', file);
                    const response = await axios.post(`${API}/upload`, formData);
                    return response.data.file_id;
                });
                const fileIds = await Promise.all(uploadPromises);
                setUploadedFiles([...uploadedFiles, ...fileIds]);
                toast.success('Files uploaded');
            } catch (error) {
                toast.error('Upload failed');
            }
        }
    });

    const handleSaveNotes = async () => {
        if (!selectedBooking || !vetNotes.trim()) {
            toast.error('Please enter notes');
            return;
        }

        try {
            // Save main notes with follow-up
            await axios.post(`${API}/vet-notes`, {
                booking_id: selectedBooking.id,
                notes: vetNotes,
                attachments: uploadedFiles,
                follow_up_date: followUpDate || null
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // If editing existing note, update it
            if (editingNoteId) {
                await axios.put(`${API}/medical-history`, {
                    booking_id: selectedBooking.id,
                    note_id: editingNoteId,
                    date: noteDate,
                    notes: vetNotes,
                    attachments: uploadedFiles,
                    follow_up_date: followUpDate || null
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Note updated successfully');
            } 
            // If adding new note
            else if (noteDate && isAddingNewNote) {
                await axios.post(`${API}/medical-history`, {
                    booking_id: selectedBooking.id,
                    date: noteDate,
                    notes: vetNotes,
                    attachments: uploadedFiles,
                    follow_up_date: followUpDate || null
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('New note added successfully');
            } else {
                toast.success('Notes saved successfully');
            }

            setIsEditDialogOpen(false);
            fetchBookings();
            fetchPendingFollowUps();
            resetNoteForm();
        } catch (error) {
            toast.error('Failed to save notes');
        }
    };

    const resetNoteForm = () => {
        setVetNotes('');
        setUploadedFiles([]);
        setFollowUpDate('');
        setNoteDate('');
        setEditingNoteId(null);
        setIsAddingNewNote(false);
    };

    const startEditingNote = (note) => {
        setEditingNoteId(note.id);
        setNoteDate(note.date);
        setVetNotes(note.notes);
        setUploadedFiles(note.attachments || []);
        setFollowUpDate(note.follow_up_date || '');
        setIsAddingNewNote(false);
    };

    const startAddingNewNote = () => {
        setEditingNoteId(null);
        setNoteDate(format(new Date(), 'yyyy-MM-dd'));
        setVetNotes('');
        setUploadedFiles([]);
        setFollowUpDate('');
        setIsAddingNewNote(true);
    };

    const handleDeleteNote = async (noteId) => {
        if (!selectedBooking) return;
        
        if (!window.confirm('Are you sure you want to delete this note?')) return;
        
        try {
            await axios.delete(`${API}/medical-history/${selectedBooking.id}/${noteId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Note deleted');
            fetchBookings();
            // Refresh the selected booking
            const response = await axios.get(`${API}/bookings/with-notes`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const updatedBooking = response.data.find(b => b.id === selectedBooking.id);
            if (updatedBooking) {
                setSelectedBooking(updatedBooking);
            }
            resetNoteForm();
        } catch (error) {
            toast.error('Failed to delete note');
        }
    };

    const openEditDialog = (booking) => {
        setSelectedBooking(booking);
        setVetNotes(booking.vet_notes || '');
        setUploadedFiles(booking.vet_attachments || []);
        setFollowUpDate(booking.follow_up_date || '');
        setNoteDate(format(new Date(), 'yyyy-MM-dd'));
        setEditingNoteId(null);
        setIsAddingNewNote(false);
        setIsEditDialogOpen(true);
    };

    const openEmailDialog = (booking, type = 'confirmation') => {
        setSelectedBooking(booking);
        setEmailType(type);
        setIsEmailDialogOpen(true);
    };

    // Mark follow-up as notified
    const markFollowUpNotified = async (bookingId) => {
        try {
            await axios.post(`${API}/follow-ups/mark-notified/${bookingId}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchPendingFollowUps();
            toast.success('Follow-up marked as notified');
        } catch (error) {
            toast.error('Failed to mark follow-up');
        }
    };

    const handleLogout = () => {
        localStorage.clear();
        navigate('/');
        toast.success('Logged out');
    };

    const verifyPayment = async (bookingId, approved) => {
        try {
            await axios.post(`${API}/payments/verify?booking_id=${bookingId}&approved=${approved}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(approved ? 'Payment approved' : 'Payment rejected');
            fetchBookings();
        } catch (error) {
            toast.error('Failed to update payment');
        }
    };

    const openDeleteDialog = (booking) => {
        setBookingToDelete(booking);
        setIsDeleteDialogOpen(true);
    };

    const handleDeleteBooking = async () => {
        if (!bookingToDelete) return;
        
        try {
            await axios.delete(`${API}/bookings/${bookingToDelete.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(`Booking ${bookingToDelete.booking_reference} deleted`);
            setIsDeleteDialogOpen(false);
            setBookingToDelete(null);
            fetchBookings();
        } catch (error) {
            toast.error('Failed to delete booking');
        }
    };

    const handleClearAllBookings = async () => {
        setIsClearing(true);
        try {
            const response = await axios.delete(`${API}/bookings/clear-all`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(response.data.message);
            setIsClearAllDialogOpen(false);
            fetchBookings();
        } catch (error) {
            toast.error('Failed to clear all bookings');
        } finally {
            setIsClearing(false);
        }
    };

    // Generate email templates
    const generateEmailTemplate = (booking, type) => {
        const clientName = booking.client?.full_name || 'Valued Client';
        const petName = booking.pet?.name || 'your pet';
        const bookingRef = booking.booking_reference || '';
        const scheduleDate = booking.consultation?.preferred_schedule_date || '';
        const scheduleTime = booking.consultation?.preferred_schedule_time || '';
        const platform = booking.consultation?.preferred_platform?.toUpperCase() || '';
        const fee = booking.consultation_fee || 0;
        const consultationType = booking.consultation?.consultation_type === 'general' ? 'General Consultation' : 'Emergency Consultation';

        if (type === 'confirmation') {
            return `Subject: Booking Confirmation - ${bookingRef} | MVC Vet Telecare

Dear ${clientName},

Thank you for booking an online consultation with MVC Vet Telecare!

📋 BOOKING DETAILS:
• Reference: ${bookingRef}
• Pet: ${petName}
• Type: ${consultationType}
• Schedule: ${scheduleDate} at ${scheduleTime}
• Platform: ${platform}
• Fee: ₱${fee}

💳 PAYMENT INSTRUCTIONS:
Please send your payment to any of the following:
• GCash: 09622526114
• Maya: +639770072496
• Bank Transfer (RCBC): 7591068636

After payment, please reply to this message with your proof of payment (screenshot).

Once verified, we will send you the consultation link.

Thank you for choosing MVC Vet Telecare!

Best regards,
MVC Vet Telecare Team
Licensed Veterinarians`;
        } else if (type === 'payment_confirmed') {
            return `Subject: Payment Confirmed - ${bookingRef} | MVC Vet Telecare

Dear ${clientName},

Great news! Your payment for booking ${bookingRef} has been confirmed. ✅

📋 CONSULTATION DETAILS:
• Pet: ${petName}
• Schedule: ${scheduleDate} at ${scheduleTime}
• Platform: ${platform}

We will send you the ${platform} link before your scheduled consultation time.

Please make sure:
✓ You have a stable internet connection
✓ Your ${platform} app is installed and working
✓ You're in a quiet place during the consultation
✓ Have photos/videos of your pet's condition ready

If you need to reschedule, please contact us at least 24 hours before your appointment.

See you soon!

Best regards,
MVC Vet Telecare Team`;
        } else if (type === 'consultation_link') {
            return `Subject: Your Consultation Link - ${bookingRef} | MVC Vet Telecare

Dear ${clientName},

Your online consultation is scheduled for TODAY!

📋 DETAILS:
• Pet: ${petName}
• Time: ${scheduleTime}
• Platform: ${platform}

🔗 JOIN YOUR CONSULTATION:
[INSERT YOUR ${platform} LINK HERE]

Please join 5 minutes before your scheduled time.

Tips for a smooth consultation:
✓ Ensure good lighting so we can see your pet clearly
✓ Have your pet nearby and calm
✓ Prepare any questions you want to ask
✓ Keep photos/videos ready to share if needed

See you shortly!

Best regards,
Dr. Kathrine Monato
MVC Vet Telecare`;
        }
        return '';
    };

    // Generate WhatsApp/Viber message
    const generateQuickMessage = (booking, type = 'confirmation') => {
        const clientName = booking.client?.full_name || 'there';
        const petName = booking.pet?.name || 'your pet';
        const bookingRef = booking.booking_reference || '';
        const scheduleDate = booking.consultation?.preferred_schedule_date || '';
        const scheduleTime = booking.consultation?.preferred_schedule_time || '';
        const fee = booking.consultation_fee || 0;

        if (type === 'confirmation') {
            return `Hi ${clientName}! 👋

Thank you for booking with MVC Vet Telecare!

📋 Booking: ${bookingRef}
🐾 Pet: ${petName}
📅 Schedule: ${scheduleDate} at ${scheduleTime}
💰 Fee: ₱${fee}

Payment options:
• GCash: 09622526114
• Maya: +639770072496

Please send proof of payment here. Thank you! 🙏`;
        } else if (type === 'payment_confirmed') {
            return `Hi ${clientName}! ✅

Your payment for ${bookingRef} is confirmed!

📅 Consultation: ${scheduleDate} at ${scheduleTime}

We'll send you the consultation link before your scheduled time. See you! 🐕🐈`;
        }
        return '';
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard! Paste in your email app.');
    };

    const openWhatsApp = (booking, messageType = 'confirmation') => {
        const phone = booking.client?.mobile_number?.replace(/[^0-9]/g, '') || '';
        // Convert PH number format
        let formattedPhone = phone;
        if (phone.startsWith('09')) {
            formattedPhone = '63' + phone.substring(1);
        } else if (phone.startsWith('0')) {
            formattedPhone = '63' + phone.substring(1);
        } else if (!phone.startsWith('63')) {
            formattedPhone = '63' + phone;
        }
        const message = encodeURIComponent(generateQuickMessage(booking, messageType));
        window.open(`https://wa.me/${formattedPhone}?text=${message}`, '_blank');
    };

    const openViber = (booking) => {
        const phone = booking.client?.mobile_number?.replace(/[^0-9]/g, '') || '';
        let formattedPhone = phone;
        if (phone.startsWith('09')) {
            formattedPhone = '63' + phone.substring(1);
        } else if (phone.startsWith('0')) {
            formattedPhone = '63' + phone.substring(1);
        } else if (!phone.startsWith('63')) {
            formattedPhone = '63' + phone;
        }
        window.open(`viber://chat?number=%2B${formattedPhone}`, '_blank');
        toast.info('Opening Viber... Copy the message if needed!');
    };

    // CSV Export functionality - includes ALL client details
    const exportToCSV = () => {
        if (bookings.length === 0) {
            toast.error('No bookings to export');
            return;
        }

        const headers = [
            'Booking Reference', 
            'Date Created', 
            'Client Name', 
            'Client Email', 
            'Client Phone',
            'Client Address', 
            'Pet Name', 
            'Pet Species', 
            'Pet Breed', 
            'Pet Age', 
            'Pet Weight',
            'Pet Gender',
            'Spayed/Neutered',
            'Birth Year',
            'Consultation Type', 
            'Schedule Date', 
            'Schedule Time', 
            'Platform', 
            'Main Concern',
            'Symptom Duration', 
            'Appetite', 
            'Prescription Needed', 
            'Media Files Count',
            'Payment Status',
            'Payment Method',
            'Booking Status', 
            'Consultation Fee', 
            'Follow-up Date',
            'Vet Notes',
            'Notes Updated At'
        ];

        const csvData = bookings.map(booking => [
            booking.booking_reference || '',
            booking.created_at ? format(new Date(booking.created_at), 'yyyy-MM-dd HH:mm') : '',
            booking.client?.full_name || '',
            booking.client?.email || '',
            booking.client?.mobile_number || '',
            `"${(booking.client?.complete_address || '').replace(/"/g, '""')}"`,
            booking.pet?.name || '',
            booking.pet?.species || '',
            booking.pet?.breed || '',
            booking.pet?.age || '',
            `${booking.pet?.weight || ''} ${booking.pet?.weight_unit || 'kg'}`,
            booking.pet?.gender || '',
            booking.pet?.spayed_neutered || '',
            booking.pet?.birth_year || '',
            booking.consultation?.consultation_type || '',
            booking.consultation?.preferred_schedule_date || '',
            booking.consultation?.preferred_schedule_time || '',
            booking.consultation?.preferred_platform || '',
            `"${(booking.consultation?.main_concern || '').replace(/"/g, '""')}"`,
            booking.consultation?.symptom_duration || '',
            booking.consultation?.appetite || '',
            booking.consultation?.prescription_needed ? 'Yes' : 'No',
            booking.consultation?.media_files?.length || 0,
            booking.payment_status || '',
            booking.payment_method || '',
            booking.booking_status || '',
            booking.consultation_fee ? `₱${booking.consultation_fee}` : '',
            booking.follow_up_date || '',
            `"${(booking.vet_notes || '').replace(/"/g, '""')}"`,
            booking.notes_updated_at || ''
        ]);

        const csvContent = [headers.join(','), ...csvData.map(row => row.join(','))].join('\n');
        const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `MVC_Bookings_${format(new Date(), 'yyyy-MM-dd')}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Bookings exported to CSV with all details');
    };

    // Generate PDF for patient notes
    const downloadNotesPDF = (booking) => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        
        // Header
        doc.setFillColor(112, 26, 37); // Maroon
        doc.rect(0, 0, pageWidth, 40, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(20);
        doc.text('MVC Vet Telecare', 14, 20);
        doc.setFontSize(12);
        doc.text('Medical Record / Patient Notes', 14, 30);
        
        // Reset text color
        doc.setTextColor(0, 0, 0);
        let y = 50;
        
        // Booking Reference
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text(`Reference: ${booking.booking_reference}`, 14, y);
        y += 10;
        
        // Date
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text(`Date: ${booking.consultation?.preferred_schedule_date || 'N/A'}`, 14, y);
        y += 15;
        
        // Client Info Section
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('Client Information', 14, y);
        y += 7;
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text(`Name: ${booking.client?.full_name || 'N/A'}`, 14, y);
        y += 5;
        doc.text(`Email: ${booking.client?.email || 'N/A'}`, 14, y);
        y += 5;
        doc.text(`Phone: ${booking.client?.mobile_number || 'N/A'}`, 14, y);
        y += 5;
        doc.text(`Address: ${booking.client?.complete_address || 'N/A'}`, 14, y);
        y += 15;
        
        // Pet Info Section
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('Patient Information', 14, y);
        y += 7;
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text(`Name: ${booking.pet?.name || 'N/A'}`, 14, y);
        y += 5;
        doc.text(`Species: ${booking.pet?.species || 'N/A'} | Breed: ${booking.pet?.breed || 'N/A'}`, 14, y);
        y += 5;
        doc.text(`Age: ${booking.pet?.age || 'N/A'} years | Weight: ${booking.pet?.weight || 'N/A'} ${booking.pet?.weight_unit || 'kg'}`, 14, y);
        y += 5;
        doc.text(`Gender: ${booking.pet?.gender || 'N/A'} | Spayed/Neutered: ${booking.pet?.spayed_neutered || 'N/A'}`, 14, y);
        y += 15;
        
        // Consultation Details
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('Consultation Details', 14, y);
        y += 7;
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text(`Type: ${booking.consultation?.consultation_type === 'general' ? 'General Consultation' : 'Emergency Consultation'}`, 14, y);
        y += 5;
        doc.text(`Main Concern: ${booking.consultation?.main_concern || 'N/A'}`, 14, y, { maxWidth: pageWidth - 28 });
        y += 10;
        doc.text(`Symptom Duration: ${booking.consultation?.symptom_duration || 'N/A'}`, 14, y);
        y += 5;
        doc.text(`Appetite: ${booking.consultation?.appetite || 'N/A'}`, 14, y);
        y += 15;
        
        // Veterinary Notes
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('Veterinary Notes', 14, y);
        y += 7;
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        
        const notes = booking.vet_notes || 'No notes added yet.';
        const splitNotes = doc.splitTextToSize(notes, pageWidth - 28);
        doc.text(splitNotes, 14, y);
        y += splitNotes.length * 5 + 10;
        
        // Medical History
        if (booking.medical_history && booking.medical_history.length > 0) {
            if (y > 250) {
                doc.addPage();
                y = 20;
            }
            doc.setFontSize(12);
            doc.setFont(undefined, 'bold');
            doc.text('Medical History', 14, y);
            y += 7;
            
            booking.medical_history.forEach((note, idx) => {
                if (y > 270) {
                    doc.addPage();
                    y = 20;
                }
                doc.setFontSize(10);
                doc.setFont(undefined, 'bold');
                doc.text(`${note.date}:`, 14, y);
                y += 5;
                doc.setFont(undefined, 'normal');
                const histNotes = doc.splitTextToSize(note.notes, pageWidth - 28);
                doc.text(histNotes, 14, y);
                y += histNotes.length * 5 + 5;
                if (note.follow_up_date) {
                    doc.text(`Follow-up: ${note.follow_up_date}`, 14, y);
                    y += 5;
                }
                y += 5;
            });
        }
        
        // Follow-up Date
        if (booking.follow_up_date) {
            if (y > 270) {
                doc.addPage();
                y = 20;
            }
            doc.setFontSize(11);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(112, 26, 37);
            doc.text(`Next Follow-up: ${booking.follow_up_date}`, 14, y);
        }
        
        // Footer
        doc.setTextColor(128, 128, 128);
        doc.setFontSize(8);
        doc.text(`Generated on ${format(new Date(), 'MMMM d, yyyy HH:mm')} | MVC Vet Telecare`, 14, 285);
        
        // Save
        doc.save(`MVC_Notes_${booking.booking_reference}_${format(new Date(), 'yyyyMMdd')}.pdf`);
        toast.success('PDF downloaded successfully');
    };

    // Open Facebook Messenger
    const openMessenger = (booking) => {
        const phone = booking.client?.mobile_number?.replace(/[^0-9]/g, '') || '';
        // Facebook Messenger uses phone numbers or Facebook IDs
        // For phone-based contacts, we'll open m.me with the message
        const message = encodeURIComponent(generateQuickMessage(booking, booking.payment_status === 'paid' ? 'payment_confirmed' : 'confirmation'));
        // Open Messenger - user will need to search for the contact
        window.open(`https://m.me/?text=${message}`, '_blank');
        toast.info('Opening Messenger... Search for the client to send the message.');
    };

    // Calendar functions
    const getDaysInMonth = () => {
        const start = startOfMonth(currentMonth);
        const end = endOfMonth(currentMonth);
        return eachDayOfInterval({ start, end });
    };

    const getBookingsForDay = (date) => {
        return bookings.filter(booking => {
            if (!booking.consultation?.preferred_schedule_date) return false;
            try {
                const bookingDate = parseISO(booking.consultation.preferred_schedule_date);
                return isSameDay(bookingDate, date);
            } catch {
                return false;
            }
        });
    };

    const handleDayClick = (date) => {
        const dayBookings = getBookingsForDay(date);
        if (dayBookings.length > 0) {
            setSelectedDate(date);
            setSelectedDayBookings(dayBookings);
            setIsDayDialogOpen(true);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'confirmed': return 'bg-green-500';
            case 'pending_verification': return 'bg-yellow-500';
            case 'cancelled': return 'bg-red-500';
            default: return 'bg-blue-500';
        }
    };

    const firstDayOfMonth = startOfMonth(currentMonth);
    const startOffset = firstDayOfMonth.getDay();

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#fcf6e3' }}>
            {/* New Booking Alert Banner - Rings until stopped */}
            {(newBookingAlert || alarmRinging) && (
                <div className="fixed top-0 left-0 right-0 bg-red-500 text-white py-4 px-6 z-50 flex items-center justify-between shadow-lg">
                    <div className="flex items-center gap-3">
                        <BellRing className="w-8 h-8 animate-bounce" />
                        <div>
                            <span className="font-bold text-lg">
                                🔔 NEW BOOKING ALERT!
                            </span>
                            {newBookingAlert && (
                                <p className="text-sm">
                                    {newBookingAlert.client?.full_name} booked for {newBookingAlert.pet?.name} - {newBookingAlert.booking_reference}
                                </p>
                            )}
                        </div>
                    </div>
                    <Button 
                        size="lg" 
                        variant="secondary"
                        onClick={stopAlarm}
                        className="bg-white text-red-600 hover:bg-gray-100 font-bold"
                    >
                        <BellOff className="w-5 h-5 mr-2" /> STOP ALARM
                    </Button>
                </div>
            )}

            {/* Follow-up Reminders Banner */}
            {pendingFollowUps.length > 0 && !alarmRinging && (
                <div className={`fixed ${newBookingAlert ? 'top-20' : 'top-0'} left-0 right-0 bg-orange-500 text-white py-3 px-6 z-40 flex items-center justify-between`}>
                    <div className="flex items-center gap-3">
                        <CalendarIcon className="w-6 h-6" />
                        <span className="font-semibold">
                            📅 {pendingFollowUps.length} Follow-up{pendingFollowUps.length > 1 ? 's' : ''} due today! 
                            {pendingFollowUps[0] && ` - ${pendingFollowUps[0].pet?.name} (${pendingFollowUps[0].client?.full_name})`}
                        </span>
                    </div>
                    <div className="flex gap-2">
                        <Button 
                            size="sm" 
                            variant="secondary"
                            onClick={() => {
                                if (pendingFollowUps[0]) {
                                    openEmailDialog(pendingFollowUps[0], 'follow_up');
                                }
                            }}
                        >
                            <Mail className="w-4 h-4 mr-1" /> Contact
                        </Button>
                        <Button 
                            size="sm" 
                            variant="secondary"
                            onClick={() => pendingFollowUps[0] && markFollowUpNotified(pendingFollowUps[0].id)}
                        >
                            <Check className="w-4 h-4 mr-1" /> Mark Done
                        </Button>
                    </div>
                </div>
            )}

            <header className="glass-header py-4" style={{ marginTop: (newBookingAlert || alarmRinging) ? '72px' : pendingFollowUps.length > 0 ? '52px' : '0' }}>
                <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif', color: '#701a25' }}>
                            Admin Dashboard
                        </h1>
                        <p className="text-sm" style={{ color: '#d9a520' }}>Welcome, {userEmail}</p>
                    </div>
                    <div className="flex gap-2 items-center">
                        {/* Notification Controls */}
                        <div className="flex gap-1 mr-2">
                            <Button
                                onClick={notificationsEnabled ? () => setNotificationsEnabled(false) : requestNotificationPermission}
                                variant="outline"
                                size="sm"
                                className={notificationsEnabled ? 'bg-green-100 border-green-500' : ''}
                                title={notificationsEnabled ? 'Notifications ON' : 'Enable Notifications'}
                            >
                                {notificationsEnabled ? <BellRing className="w-4 h-4 text-green-600" /> : <Bell className="w-4 h-4" />}
                            </Button>
                            <Button
                                onClick={() => setSoundEnabled(!soundEnabled)}
                                variant="outline"
                                size="sm"
                                className={soundEnabled ? 'bg-blue-100 border-blue-500' : ''}
                                title={soundEnabled ? 'Sound ON' : 'Sound OFF'}
                            >
                                {soundEnabled ? <Volume2 className="w-4 h-4 text-blue-600" /> : <VolumeX className="w-4 h-4" />}
                            </Button>
                        </div>
                        <Button onClick={exportToCSV} variant="outline" className="hidden sm:flex">
                            <Download className="mr-2 w-4 h-4" /> Export CSV
                        </Button>
                        <Button onClick={handleLogout} variant="outline">
                            <LogOut className="mr-2 w-4 h-4" /> Logout
                        </Button>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-6 py-8">
                {/* Notification Status Banner */}
                {!notificationsEnabled && (
                    <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Bell className="w-5 h-5 text-yellow-600" />
                            <span className="text-sm text-yellow-800">
                                Enable notifications to get alerted when clients book!
                            </span>
                        </div>
                        <Button size="sm" onClick={requestNotificationPermission} className="bg-yellow-500 hover:bg-yellow-600">
                            Enable Notifications
                        </Button>
                    </div>
                )}

                {/* Stats Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <Card>
                        <CardContent className="pt-6">
                            <div className="text-2xl font-bold" style={{ color: '#701a25' }}>{bookings.length}</div>
                            <p className="text-sm text-muted-foreground">Total Bookings</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="text-2xl font-bold text-green-600">
                                {bookings.filter(b => b.booking_status === 'confirmed').length}
                            </div>
                            <p className="text-sm text-muted-foreground">Confirmed</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="text-2xl font-bold text-yellow-600">
                                {bookings.filter(b => b.payment_status === 'pending_verification').length}
                            </div>
                            <p className="text-sm text-muted-foreground">Pending Verification</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="text-2xl font-bold" style={{ color: '#d9a520' }}>
                                ₱{bookings.reduce((sum, b) => sum + (b.consultation_fee || 0), 0).toLocaleString()}
                            </div>
                            <p className="text-sm text-muted-foreground">Total Revenue</p>
                        </CardContent>
                    </Card>
                </div>

                {/* Action Buttons Row */}
                <div className="flex gap-2 mb-6 flex-wrap">
                    <Button onClick={exportToCSV} variant="outline" className="sm:hidden">
                        <Download className="mr-2 w-4 h-4" /> Export CSV
                    </Button>
                    {bookings.length > 0 && (
                        <Button onClick={() => setIsClearAllDialogOpen(true)} variant="destructive">
                            <Trash2 className="mr-2 w-4 h-4" /> Clear All Data
                        </Button>
                    )}
                </div>

                {/* Tabs for List/Calendar View */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="grid w-full grid-cols-2 mb-6">
                        <TabsTrigger value="list" className="flex items-center gap-2">
                            <List className="w-4 h-4" /> List View
                        </TabsTrigger>
                        <TabsTrigger value="calendar" className="flex items-center gap-2">
                            <CalendarIcon className="w-4 h-4" /> Calendar View
                        </TabsTrigger>
                    </TabsList>

                    {/* List View */}
                    <TabsContent value="list">
                        <div className="grid gap-6">
                            {loading ? (
                                <p>Loading bookings...</p>
                            ) : bookings.length === 0 ? (
                                <Card>
                                    <CardContent className="py-12 text-center">
                                        <CalendarIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                        <p className="text-muted-foreground">No bookings yet</p>
                                    </CardContent>
                                </Card>
                            ) : (
                                bookings.map((booking) => (
                                    <Card key={booking.id}>
                                        <CardHeader>
                                            <div className="flex justify-between items-start flex-wrap gap-2">
                                                <div>
                                                    <CardTitle className="text-xl" style={{ color: '#701a25' }}>
                                                        {booking.booking_reference} - {booking.pet?.name || 'N/A'}
                                                    </CardTitle>
                                                    <div className="flex gap-4 mt-2 text-sm text-muted-foreground">
                                                        <div className="flex items-center gap-1">
                                                            <CalendarIcon className="w-4 h-4" />
                                                            {booking.consultation?.preferred_schedule_date || 'N/A'} at {booking.consultation?.preferred_schedule_time || 'N/A'}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex gap-2 flex-wrap">
                                                    <Button onClick={() => openEditDialog(booking)} size="sm">
                                                        <Edit2 className="w-4 h-4 mr-1" /> Notes
                                                    </Button>
                                                    <Button onClick={() => openEmailDialog(booking, 'confirmation')} size="sm" variant="outline">
                                                        <Mail className="w-4 h-4 mr-1" /> Email
                                                    </Button>
                                                    <Button onClick={() => openDeleteDialog(booking)} size="sm" variant="destructive">
                                                        <Trash2 className="w-4 h-4 mr-1" /> Delete
                                                    </Button>
                                                </div>
                                            </div>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="grid md:grid-cols-2 gap-4">
                                                <div>
                                                    <h4 className="font-semibold mb-2">Client Info</h4>
                                                    <p className="text-sm flex items-center gap-2"><Mail className="w-4 h-4" />{booking.client?.email || 'N/A'}</p>
                                                    <p className="text-sm flex items-center gap-2"><Phone className="w-4 h-4" />{booking.client?.mobile_number || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <h4 className="font-semibold mb-2">Pet Info</h4>
                                                    <p className="text-sm">{booking.pet?.species || 'N/A'} - {booking.pet?.breed || 'N/A'}</p>
                                                    <p className="text-sm">Age: {booking.pet?.age || 'N/A'}y | Weight: {booking.pet?.weight || 'N/A'} {booking.pet?.weight_unit || 'kg'}</p>
                                                    <p className="text-sm">Gender: {booking.pet?.gender || 'N/A'}</p>
                                                </div>
                                            </div>
                                            <div className="mt-4">
                                                <h4 className="font-semibold mb-2">Main Concern</h4>
                                                <p className="text-sm">{booking.consultation?.main_concern || 'N/A'}</p>
                                            </div>

                                            {/* Symptom Images/Videos from Client */}
                                            {booking.consultation?.media_files && booking.consultation.media_files.length > 0 && (
                                                <div className="mt-4">
                                                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                                                        <ImageIcon className="w-4 h-4" /> Symptom Images/Videos ({booking.consultation.media_files.length})
                                                    </h4>
                                                    <div className="flex gap-2 flex-wrap">
                                                        {booking.consultation.media_files.map((fileId, idx) => (
                                                            <a 
                                                                key={idx}
                                                                href={`${API}/download/${fileId}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="block"
                                                            >
                                                                <div className="w-20 h-20 border rounded-lg overflow-hidden hover:border-blue-500 transition-colors cursor-pointer bg-gray-100 flex items-center justify-center">
                                                                    <img 
                                                                        src={`${API}/download/${fileId}`}
                                                                        alt={`Symptom ${idx + 1}`}
                                                                        className="w-full h-full object-cover"
                                                                        onError={(e) => {
                                                                            e.target.style.display = 'none';
                                                                            e.target.nextSibling.style.display = 'flex';
                                                                        }}
                                                                    />
                                                                    <div className="hidden flex-col items-center justify-center text-xs text-gray-500">
                                                                        <FileText className="w-6 h-6 mb-1" />
                                                                        <span>File {idx + 1}</span>
                                                                    </div>
                                                                </div>
                                                            </a>
                                                        ))}
                                                    </div>
                                                    <p className="text-xs text-muted-foreground mt-2">Click to view/download</p>
                                                </div>
                                            )}

                                            {/* Payment Proof */}
                                            {booking.payment_proof && (
                                                <div className="mt-4">
                                                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                                                        <FileText className="w-4 h-4" /> Payment Proof
                                                    </h4>
                                                    <a 
                                                        href={`${API}/download/${booking.payment_proof}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                                                    >
                                                        <img 
                                                            src={`${API}/download/${booking.payment_proof}`}
                                                            alt="Payment Proof"
                                                            className="w-20 h-20 object-cover rounded border"
                                                            onError={(e) => { e.target.style.display = 'none'; }}
                                                        />
                                                        <span>View Payment Proof</span>
                                                    </a>
                                                </div>
                                            )}
                                            
                                            {/* Quick Contact Buttons */}
                                            <div className="mt-4 flex gap-2 flex-wrap">
                                                <Button 
                                                    onClick={() => openWhatsApp(booking, booking.payment_status === 'paid' ? 'payment_confirmed' : 'confirmation')} 
                                                    size="sm" 
                                                    className="bg-green-600 hover:bg-green-700"
                                                >
                                                    <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
                                                </Button>
                                                <Button 
                                                    onClick={() => openViber(booking)} 
                                                    size="sm" 
                                                    className="bg-purple-600 hover:bg-purple-700"
                                                >
                                                    <Phone className="w-4 h-4 mr-1" /> Viber
                                                </Button>
                                                <Button 
                                                    onClick={() => openMessenger(booking)} 
                                                    size="sm" 
                                                    className="bg-blue-600 hover:bg-blue-700"
                                                >
                                                    <Facebook className="w-4 h-4 mr-1" /> Messenger
                                                </Button>
                                            </div>

                                            {/* Follow-up Date Badge */}
                                            {booking.follow_up_date && (
                                                <div className="mt-2">
                                                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800">
                                                        📅 Follow-up: {booking.follow_up_date}
                                                    </span>
                                                </div>
                                            )}

                                            {/* Status badges */}
                                            <div className="mt-4 flex gap-2 flex-wrap">
                                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                                    booking.booking_status === 'confirmed' 
                                                        ? 'bg-green-100 text-green-800' 
                                                        : booking.booking_status === 'cancelled'
                                                        ? 'bg-red-100 text-red-800'
                                                        : 'bg-yellow-100 text-yellow-800'
                                                }`}>
                                                    {booking.booking_status || 'pending'}
                                                </span>
                                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                                    booking.payment_status === 'paid' 
                                                        ? 'bg-green-100 text-green-800' 
                                                        : booking.payment_status === 'rejected'
                                                        ? 'bg-red-100 text-red-800'
                                                        : 'bg-blue-100 text-blue-800'
                                                }`}>
                                                    Payment: {booking.payment_status || 'pending'}
                                                </span>
                                                {booking.vet_notes && (
                                                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                                                        Has Notes
                                                    </span>
                                                )}
                                            </div>

                                            {booking.payment_status === 'pending_verification' && (
                                                <div className="mt-4 flex gap-2">
                                                    <Button onClick={() => verifyPayment(booking.id, true)} size="sm" className="bg-green-600 hover:bg-green-700">
                                                        <Check className="w-4 h-4 mr-1" /> Approve Payment
                                                    </Button>
                                                    <Button onClick={() => verifyPayment(booking.id, false)} size="sm" variant="destructive">
                                                        <X className="w-4 h-4 mr-1" /> Reject
                                                    </Button>
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>
                                ))
                            )}
                        </div>
                    </TabsContent>

                    {/* Calendar View */}
                    <TabsContent value="calendar">
                        <Card>
                            <CardHeader>
                                <div className="flex justify-between items-center">
                                    <Button variant="outline" size="sm" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
                                        <ChevronLeft className="w-4 h-4" />
                                    </Button>
                                    <h2 className="text-xl font-bold" style={{ color: '#701a25' }}>
                                        {format(currentMonth, 'MMMM yyyy')}
                                    </h2>
                                    <Button variant="outline" size="sm" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
                                        <ChevronRight className="w-4 h-4" />
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-7 gap-1 mb-2">
                                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                                        <div key={day} className="text-center text-sm font-semibold py-2" style={{ color: '#701a25' }}>
                                            {day}
                                        </div>
                                    ))}
                                </div>
                                
                                <div className="grid grid-cols-7 gap-1">
                                    {Array.from({ length: startOffset }).map((_, idx) => (
                                        <div key={`empty-${idx}`} className="h-24 bg-gray-50 rounded-lg"></div>
                                    ))}
                                    
                                    {getDaysInMonth().map((date) => {
                                        const dayBookings = getBookingsForDay(date);
                                        const isToday = isSameDay(date, new Date());
                                        
                                        return (
                                            <div
                                                key={date.toISOString()}
                                                onClick={() => handleDayClick(date)}
                                                className={`h-24 p-1 rounded-lg border transition-colors cursor-pointer hover:bg-gray-50 ${
                                                    isToday ? 'border-2 border-yellow-500 bg-yellow-50' : 'border-gray-200'
                                                } ${dayBookings.length > 0 ? 'bg-blue-50' : ''}`}
                                            >
                                                <div className={`text-sm font-medium ${isToday ? 'text-yellow-700' : ''}`}>
                                                    {format(date, 'd')}
                                                </div>
                                                <div className="mt-1 space-y-1 overflow-hidden">
                                                    {dayBookings.slice(0, 2).map((booking) => (
                                                        <div
                                                            key={booking.id}
                                                            className={`text-xs px-1 py-0.5 rounded truncate text-white ${getStatusColor(booking.booking_status)}`}
                                                        >
                                                            {booking.consultation?.preferred_schedule_time} - {booking.pet?.name}
                                                        </div>
                                                    ))}
                                                    {dayBookings.length > 2 && (
                                                        <div className="text-xs text-muted-foreground px-1">
                                                            +{dayBookings.length - 2} more
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                <div className="mt-4 flex gap-4 justify-center text-xs">
                                    <div className="flex items-center gap-1">
                                        <div className="w-3 h-3 rounded bg-green-500"></div>
                                        <span>Confirmed</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <div className="w-3 h-3 rounded bg-yellow-500"></div>
                                        <span>Pending Verification</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <div className="w-3 h-3 rounded bg-blue-500"></div>
                                        <span>Pending</span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>

            {/* Edit Notes Dialog */}
            <Dialog open={isEditDialogOpen} onOpenChange={(open) => { setIsEditDialogOpen(open); if (!open) resetNoteForm(); }}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center justify-between">
                            <span>Medical Notes - {selectedBooking?.pet?.name}</span>
                            <Button variant="outline" size="sm" onClick={() => selectedBooking && downloadNotesPDF(selectedBooking)}>
                                <FileDown className="w-4 h-4 mr-1" /> Download PDF
                            </Button>
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        {/* Patient Info Summary */}
                        <div className="bg-gray-50 p-3 rounded-lg text-sm">
                            <div className="grid grid-cols-2 gap-2">
                                <p><strong>Pet:</strong> {selectedBooking?.pet?.name} ({selectedBooking?.pet?.species})</p>
                                <p><strong>Weight:</strong> {selectedBooking?.pet?.weight || 'N/A'} {selectedBooking?.pet?.weight_unit || 'kg'}</p>
                                <p><strong>Owner:</strong> {selectedBooking?.client?.full_name}</p>
                                <p><strong>Ref:</strong> {selectedBooking?.booking_reference}</p>
                            </div>
                        </div>

                        {/* Medical History Timeline with Edit/Delete */}
                        {selectedBooking?.medical_history && selectedBooking.medical_history.length > 0 && (
                            <div className="border rounded-lg p-3">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-semibold flex items-center gap-2">
                                        <CalendarIcon className="w-4 h-4" /> Medical History ({selectedBooking.medical_history.length} notes)
                                    </h4>
                                    <Button size="sm" variant="outline" onClick={startAddingNewNote}>
                                        <Plus className="w-4 h-4 mr-1" /> Add New Note
                                    </Button>
                                </div>
                                <div className="space-y-3 max-h-48 overflow-y-auto">
                                    {selectedBooking.medical_history.map((note, idx) => (
                                        <div key={note.id || idx} className={`border-l-2 pl-3 py-2 rounded-r ${editingNoteId === note.id ? 'border-green-500 bg-green-50' : 'border-blue-500 hover:bg-gray-50'}`}>
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <p className="text-xs font-semibold text-blue-600">{note.date}</p>
                                                    <p className="text-sm mt-1">{note.notes}</p>
                                                    {note.attachments?.length > 0 && (
                                                        <div className="flex gap-1 mt-1">
                                                            {note.attachments.map((fileId, fIdx) => (
                                                                <a key={fIdx} href={`${API}/download/${fileId}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 underline" download>
                                                                    📎 File {fIdx + 1}
                                                                </a>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {note.follow_up_date && <p className="text-xs text-orange-600 mt-1">📅 Follow-up: {note.follow_up_date}</p>}
                                                </div>
                                                <div className="flex gap-1 ml-2">
                                                    <Button 
                                                        size="sm" 
                                                        variant={editingNoteId === note.id ? "default" : "ghost"}
                                                        onClick={() => startEditingNote(note)}
                                                        className="h-7 px-2"
                                                    >
                                                        <Edit2 className="w-3 h-3" />
                                                    </Button>
                                                    <Button 
                                                        size="sm" 
                                                        variant="ghost"
                                                        onClick={() => handleDeleteNote(note.id)}
                                                        className="h-7 px-2 text-red-500 hover:text-red-700"
                                                    >
                                                        <Trash2 className="w-3 h-3" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Add New Note Button if no history */}
                        {(!selectedBooking?.medical_history || selectedBooking.medical_history.length === 0) && (
                            <div className="border rounded-lg p-4 text-center">
                                <p className="text-sm text-muted-foreground mb-2">No medical history yet</p>
                                <Button size="sm" onClick={startAddingNewNote}>
                                    <Plus className="w-4 h-4 mr-1" /> Add First Note
                                </Button>
                            </div>
                        )}

                        {/* Edit/Add Note Form */}
                        {(isAddingNewNote || editingNoteId) && (
                            <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
                                <h4 className="font-semibold mb-3 text-blue-700">
                                    {editingNoteId ? '✏️ Editing Note' : '➕ Adding New Note'}
                                </h4>
                                
                                {/* Note Date */}
                                <div className="grid grid-cols-2 gap-4 mb-3">
                                    <div>
                                        <Label>Note Date *</Label>
                                        <Input 
                                            type="date" 
                                            value={noteDate} 
                                            onChange={(e) => setNoteDate(e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <Label>Follow-up Date</Label>
                                        <Input 
                                            type="date" 
                                            value={followUpDate} 
                                            onChange={(e) => setFollowUpDate(e.target.value)}
                                        />
                                    </div>
                                </div>

                                {/* Veterinary Notes */}
                                <div className="mb-3">
                                    <Label>Veterinary Notes *</Label>
                                    <Textarea
                                        value={vetNotes}
                                        onChange={(e) => setVetNotes(e.target.value)}
                                        rows={4}
                                        placeholder="Enter diagnosis, treatment plan, medications, follow-up instructions..."
                                    />
                                </div>

                                {/* File Upload */}
                                <div className="mb-3">
                                    <Label>Attach Files</Label>
                                    <div {...getRootProps()} className="border-2 border-dashed p-3 text-center cursor-pointer hover:bg-white rounded-lg">
                                        <input {...getInputProps()} />
                                        <Upload className="w-5 h-5 mx-auto mb-1" style={{ color: '#d9a520' }} />
                                        <p className="text-xs">Drop files or click to upload</p>
                                    </div>
                                    {uploadedFiles.length > 0 && (
                                        <div className="mt-2 flex gap-2 flex-wrap">
                                            {uploadedFiles.map((fileId, idx) => (
                                                <a key={idx} href={`${API}/download/${fileId}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 underline" download>
                                                    📎 File {idx + 1}
                                                </a>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="flex gap-2">
                                    <Button onClick={handleSaveNotes} className="flex-1 bg-blue-600 hover:bg-blue-700">
                                        {editingNoteId ? 'Update Note' : 'Save Note'}
                                    </Button>
                                    <Button variant="outline" onClick={resetNoteForm}>
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Quick Update Section (when not editing/adding) */}
                        {!isAddingNewNote && !editingNoteId && (
                            <>
                                {/* Note Date */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label>Note Date *</Label>
                                        <Input 
                                            type="date" 
                                            value={noteDate} 
                                            onChange={(e) => setNoteDate(e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <Label>Follow-up Date</Label>
                                        <Input 
                                            type="date" 
                                            value={followUpDate} 
                                            onChange={(e) => setFollowUpDate(e.target.value)}
                                        />
                                    </div>
                                </div>

                                {/* Veterinary Notes */}
                                <div>
                                    <Label>Veterinary Notes *</Label>
                                    <Textarea
                                        value={vetNotes}
                                        onChange={(e) => setVetNotes(e.target.value)}
                                        rows={6}
                                        placeholder="Enter diagnosis, treatment plan, medications, follow-up instructions..."
                                    />
                                </div>
                            </>
                        )}

                        {/* Client Symptom Images */}
                        {selectedBooking?.consultation?.media_files?.length > 0 && (
                            <div>
                                <Label className="flex items-center gap-2">
                                    <ImageIcon className="w-4 h-4" /> Client Symptom Images ({selectedBooking.consultation.media_files.length})
                                </Label>
                                <div className="flex gap-2 flex-wrap mt-2 p-2 bg-gray-50 rounded">
                                    {selectedBooking.consultation.media_files.map((fileId, idx) => (
                                        <a key={idx} href={`${API}/download/${fileId}`} target="_blank" rel="noopener noreferrer" download>
                                            <img 
                                                src={`${API}/download/${fileId}`} 
                                                alt={`Symptom ${idx + 1}`}
                                                className="w-16 h-16 object-cover rounded border hover:border-blue-500"
                                                onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ccc"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"/></svg>'; }}
                                            />
                                        </a>
                                    ))}
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">Click images to download</p>
                            </div>
                        )}

                        {/* Attach Prescriptions/Files - only when not in edit mode */}
                        {!isAddingNewNote && !editingNoteId && (
                            <div>
                                <Label>Attach Prescriptions/Files</Label>
                                <div {...getRootProps()} className="border-2 border-dashed p-4 text-center cursor-pointer hover:bg-gray-50 rounded-lg">
                                    <input {...getInputProps()} />
                                    <Upload className="w-6 h-6 mx-auto mb-1" style={{ color: '#d9a520' }} />
                                    <p className="text-sm">Drop files or click to upload</p>
                                    <p className="text-xs text-muted-foreground">PDF, PNG, JPG, MP4 accepted</p>
                                </div>
                                {uploadedFiles.length > 0 && (
                                    <div className="mt-2">
                                        <p className="text-sm text-green-600 mb-1">
                                            <FileText className="w-4 h-4 inline mr-1" />
                                            {uploadedFiles.length} file(s) attached
                                        </p>
                                        <div className="flex gap-2 flex-wrap">
                                            {uploadedFiles.map((fileId, idx) => (
                                                <a key={idx} href={`${API}/download/${fileId}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 underline" download>
                                                    Download File {idx + 1}
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Save Button - only when not in edit/add mode */}
                        {!isAddingNewNote && !editingNoteId && (
                            <Button onClick={handleSaveNotes} className="btn-primary w-full">
                                Save Notes
                            </Button>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Email Template Dialog */}
            <Dialog open={isEmailDialogOpen} onOpenChange={setIsEmailDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Mail className="w-5 h-5" /> Email Templates - {selectedBooking?.booking_reference}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        {/* Template Type Selector */}
                        <div className="flex gap-2 flex-wrap">
                            <Button 
                                variant={emailType === 'confirmation' ? 'default' : 'outline'} 
                                size="sm"
                                onClick={() => setEmailType('confirmation')}
                            >
                                Booking Confirmation
                            </Button>
                            <Button 
                                variant={emailType === 'payment_confirmed' ? 'default' : 'outline'} 
                                size="sm"
                                onClick={() => setEmailType('payment_confirmed')}
                            >
                                Payment Confirmed
                            </Button>
                            <Button 
                                variant={emailType === 'consultation_link' ? 'default' : 'outline'} 
                                size="sm"
                                onClick={() => setEmailType('consultation_link')}
                            >
                                Consultation Link
                            </Button>
                        </div>

                        {/* Email Preview */}
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <pre className="whitespace-pre-wrap text-sm font-mono">
                                {selectedBooking && generateEmailTemplate(selectedBooking, emailType)}
                            </pre>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2 flex-wrap">
                            <Button 
                                onClick={() => copyToClipboard(generateEmailTemplate(selectedBooking, emailType))}
                                className="flex-1"
                            >
                                <Copy className="w-4 h-4 mr-2" /> Copy Email Text
                            </Button>
                            <Button 
                                variant="outline"
                                onClick={() => {
                                    const subject = emailType === 'confirmation' 
                                        ? `Booking Confirmation - ${selectedBooking?.booking_reference}` 
                                        : emailType === 'payment_confirmed'
                                        ? `Payment Confirmed - ${selectedBooking?.booking_reference}`
                                        : `Consultation Link - ${selectedBooking?.booking_reference}`;
                                    const body = encodeURIComponent(generateEmailTemplate(selectedBooking, emailType));
                                    window.open(`mailto:${selectedBooking?.client?.email}?subject=${encodeURIComponent(subject)}&body=${body}`, '_blank');
                                }}
                            >
                                <ExternalLink className="w-4 h-4 mr-2" /> Open in Mail App
                            </Button>
                        </div>

                        {/* Quick Contact Section */}
                        <div className="border-t pt-4">
                            <h4 className="font-semibold mb-3">Quick Contact via Messaging Apps</h4>
                            <div className="flex gap-2">
                                <Button 
                                    onClick={() => openWhatsApp(selectedBooking, emailType === 'payment_confirmed' ? 'payment_confirmed' : 'confirmation')} 
                                    className="bg-green-600 hover:bg-green-700 flex-1"
                                >
                                    <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
                                </Button>
                                <Button 
                                    onClick={() => {
                                        copyToClipboard(generateQuickMessage(selectedBooking, emailType === 'payment_confirmed' ? 'payment_confirmed' : 'confirmation'));
                                        openViber(selectedBooking);
                                    }} 
                                    className="bg-purple-600 hover:bg-purple-700 flex-1"
                                >
                                    <Phone className="w-4 h-4 mr-2" /> Viber
                                </Button>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Day Bookings Dialog */}
            <Dialog open={isDayDialogOpen} onOpenChange={setIsDayDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>
                            Bookings for {selectedDate && format(selectedDate, 'MMMM d, yyyy')}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 max-h-96 overflow-y-auto">
                        {selectedDayBookings.map((booking) => (
                            <Card key={booking.id}>
                                <CardContent className="pt-4">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <p className="font-semibold" style={{ color: '#701a25' }}>
                                                {booking.consultation?.preferred_schedule_time} - {booking.pet?.name}
                                            </p>
                                            <p className="text-sm text-muted-foreground">
                                                {booking.booking_reference} | {booking.client?.full_name}
                                            </p>
                                            <p className="text-sm mt-1">{booking.consultation?.main_concern}</p>
                                        </div>
                                        <div className="flex flex-col gap-2 items-end">
                                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                                booking.booking_status === 'confirmed' 
                                                    ? 'bg-green-100 text-green-800' 
                                                    : 'bg-yellow-100 text-yellow-800'
                                            }`}>
                                                {booking.booking_status}
                                            </span>
                                            <div className="flex gap-1">
                                                <Button size="sm" variant="outline" onClick={() => {
                                                    setIsDayDialogOpen(false);
                                                    openEditDialog(booking);
                                                }}>
                                                    <Edit2 className="w-3 h-3" />
                                                </Button>
                                                <Button size="sm" variant="outline" onClick={() => {
                                                    setIsDayDialogOpen(false);
                                                    openEmailDialog(booking);
                                                }}>
                                                    <Mail className="w-3 h-3" />
                                                </Button>
                                                <Button size="sm" className="bg-green-600" onClick={() => openWhatsApp(booking)}>
                                                    <MessageCircle className="w-3 h-3" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-5 h-5" /> Delete Booking
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Are you sure you want to delete this booking? This action cannot be undone.
                        </p>
                        {bookingToDelete && (
                            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                                <p className="font-semibold" style={{ color: '#701a25' }}>
                                    {bookingToDelete.booking_reference}
                                </p>
                                <p className="text-sm">Client: {bookingToDelete.client?.full_name}</p>
                                <p className="text-sm">Pet: {bookingToDelete.pet?.name}</p>
                                <p className="text-sm">
                                    Schedule: {bookingToDelete.consultation?.preferred_schedule_date} at {bookingToDelete.consultation?.preferred_schedule_time}
                                </p>
                            </div>
                        )}
                        <div className="flex gap-2 justify-end">
                            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
                                Cancel
                            </Button>
                            <Button variant="destructive" onClick={handleDeleteBooking}>
                                <Trash2 className="w-4 h-4 mr-2" /> Delete Booking
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Clear All Data Confirmation Dialog */}
            <Dialog open={isClearAllDialogOpen} onOpenChange={setIsClearAllDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-5 h-5" /> Clear All Data
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Are you sure you want to <strong>delete ALL bookings</strong>? This will:
                        </p>
                        <ul className="text-sm list-disc list-inside space-y-1 text-red-600">
                            <li>Delete all {bookings.length} bookings</li>
                            <li>Reset Total Bookings to 0</li>
                            <li>Reset Confirmed count to 0</li>
                            <li>Reset Pending Verification to 0</li>
                            <li>Reset Total Revenue to ₱0</li>
                        </ul>
                        <p className="text-sm font-semibold text-red-600">
                            ⚠️ This action cannot be undone!
                        </p>
                        <div className="flex gap-2 justify-end">
                            <Button variant="outline" onClick={() => setIsClearAllDialogOpen(false)}>
                                Cancel
                            </Button>
                            <Button 
                                variant="destructive" 
                                onClick={handleClearAllBookings}
                                disabled={isClearing}
                            >
                                {isClearing ? (
                                    <>Clearing...</>
                                ) : (
                                    <><Trash2 className="w-4 h-4 mr-2" /> Clear All Data</>
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminDashboard;
